/* =========================================================================================
 * msal.ts – Vue 3 / MSAL & Power BI helper
 * -----------------------------------------------------------------------------------------
 * - Adds router awareness via initMsalRouter() so code that lives outside Vue lifecycle
 * (MSAL callbacks, setInterval, etc.) can access route params.
 * - Otherwise preserves your original public API.
 * ======================================================================================= */

import {
  EventType,
  PublicClientApplication,
  type Configuration,
  InteractionRequiredAuthError
} from '@azure/msal-browser';
import { models } from 'powerbi-client';
import type { Router } from 'vue-router';
import * as Sentry from '@sentry/vue';

import getConfig from '@/utils/config';
import { $mxbFetchWithoutPreSignIn, $mxsFetch } from '@/utils/fetch';
import { PowerBiService } from '@/utils/power-bi';
import { TokenManager } from '@/utils/token_manager';
import { naiveDiscrete } from '@/plugins/naive-ui';
import { setSentryUserContext, captureErrorWithContext } from '@/utils/sentry-context';

/* ---------------------------------------------------------------------------
 * 0. Router plumbing (NEW)
 * ------------------------------------------------------------------------- */
let _router: Router | null = null;

/** Call once from main.ts *after* you create / configure the router. */
export function initMsalRouter(router: Router): void {
  _router = router;
}

/** Safely read the current reportId param (undefined if not on that route). */
function getCurrentReportId(): string | undefined {
  return _router?.currentRoute.value.params.reportId as string | undefined;
}

/* ---------------------------------------------------------------------------
 * 1. Configuration & MSAL instance
 * ------------------------------------------------------------------------- */
const AUTH_BASE_URL = getConfig('VITE_APP_AUTHENTICATION_BASE_URL');
const AUTH_CLIENT_ID = getConfig('VITE_APP_AUTHENTICATION_CLIENT_ID');

/* Utility function to detect if we're running in an embedded context */
function isEmbeddedContext(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    /* If we can't access window.top due to cross-origin restrictions, we're likely embedded */
    return true;
  }
}

/* Determine the preferred interaction type based on context */
function getPreferredInteractionType(): 'popup' | 'redirect' {
  return isEmbeddedContext() ? 'popup' : 'redirect';
}

const msalConfig: Configuration = {
  auth: {
    clientId: AUTH_CLIENT_ID,
    redirectUri: window.location.origin,
    authority: `${AUTH_BASE_URL}/organizations`
  },
  cache: { cacheLocation: 'localStorage' },
  system: { allowRedirectInIframe: false, allowNativeBroker: false }
};

export const $msal = new PublicClientApplication(msalConfig);
/* ---------------------------------------------------------------------------
 * 2. Token-request helpers
 * ------------------------------------------------------------------------- */
const mxbTokenRequest = () => ({
  scopes: ['openid', 'profile', 'offline_access', 'api://directsellserver/.default'],
  ...($msal.getActiveAccount() ? { account: $msal.getActiveAccount()! } : {})
});

const powerBiTokenRequest = () => ({
  scopes: ['https://analysis.windows.net/powerbi/api/.default'],
  ...($msal.getActiveAccount() ? { account: $msal.getActiveAccount()! } : {})
});

/* ---------------------------------------------------------------------------
 * 3. Misc. state
 * ------------------------------------------------------------------------- */
let isMsalInteractionInProgress = false;
let powerBiTokenRefreshIntervalId: number | null = null;

/* ---------------------------------------------------------------------------
 * 4. Account helpers
 * ------------------------------------------------------------------------- */
function setActiveAccount(): void {
  const [first] = $msal.getAllAccounts();
  if (first) $msal.setActiveAccount(first);
}

export function isAuthenticated(): boolean {
  return $msal.getActiveAccount() !== null;
}

/* ---------------------------------------------------------------------------
 * 5. Token refresh helpers
 * ------------------------------------------------------------------------- */
export async function refreshTokenIfNeeded(tokenName: string): Promise<string | null> {
  const activeAccount = $msal.getActiveAccount();
  if (!activeAccount) {
    Sentry.logger.warn('Cannot refresh token: No active account', {
      log_source: 'msal',
      tokenName
    });
    return null;
  }

  if (!TokenManager.isCachedTokenExpired(tokenName)) {
    return TokenManager.getCachedToken(tokenName)?.token ?? null;
  }

  const request = tokenName === 'power_bi_access_token' ? powerBiTokenRequest() : mxbTokenRequest();

  try {
    const { accessToken } = await $msal.acquireTokenSilent(request);
    TokenManager.setCachedToken({ token: accessToken, name: tokenName });
    return accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const preferredFlow = getPreferredInteractionType();

      if (preferredFlow === 'popup') {
        try {
          const { accessToken } = await $msal.acquireTokenPopup(request);
          TokenManager.setCachedToken({ token: accessToken, name: tokenName });
          return accessToken;
        } catch (popupError) {
          captureErrorWithContext(popupError as Error, {
            operation: 'token_refresh_popup',
            tokenName,
            errorType: 'interaction_required',
            flow: 'popup'
          });
          throw popupError;
        }
      } else {
        // For redirect flow, we can't return the token immediately
        await $msal.acquireTokenRedirect(request);
        // Execution stops here as page redirects
        return null;
      }
    }
    captureErrorWithContext(error as Error, {
      operation: 'token_refresh_silent',
      tokenName,
      errorType: error instanceof Error ? error.name : 'unknown',
      hasActiveAccount: !!activeAccount
    });
    throw error;
  }
}

/* ---------------------------------------------------------------------------
 * 6. Initial sign-in
 * ------------------------------------------------------------------------- */

/**
 * Handle redirect response and store tokens if successful
 * Should be called during app initialization
 */
export async function handleRedirectResponse(): Promise<boolean> {
  try {
    const redirectResponse = await $msal.handleRedirectPromise();
    if (redirectResponse) {
      setActiveAccount();

      // Store the access token from the redirect response
      if (redirectResponse.accessToken) {
        TokenManager.setCachedToken({ token: redirectResponse.accessToken, name: 'access_token' });
      }

      // Try to get Power BI token silently
      try {
        const pbi = await $msal.acquireTokenSilent(powerBiTokenRequest());
        TokenManager.setCachedToken({ token: pbi.accessToken, name: 'power_bi_access_token' });
      } catch (pbiError) {
        Sentry.logger.warn('Could not acquire Power BI token after redirect', {
          log_source: 'msal',
          error: pbiError
        });
      }
      return true;
    }
    return false;
  } catch (error) {
    Sentry.logger.error('Error handling redirect response', { log_source: 'msal', error });
    captureErrorWithContext(error as Error, {
      operation: 'handle_redirect_response',
      errorType: error instanceof Error ? error.name : 'unknown'
    });
    return false;
  }
}

export async function performInitialSignIn(): Promise<void> {
  const preferredFlow = getPreferredInteractionType();
  try {
    // Check if we have existing accounts first (redirect response should have been handled in main.ts)
    const accounts = $msal.getAllAccounts();
    if (accounts.length) {
      $msal.setActiveAccount(accounts[0]!);

      // Attempt silent token acquisition for existing account
      try {
        const mxb = await $msal.acquireTokenSilent(mxbTokenRequest());
        const pbi = await $msal.acquireTokenSilent(powerBiTokenRequest());

        TokenManager.setCachedToken({ token: mxb.accessToken, name: 'access_token' });
        TokenManager.setCachedToken({ token: pbi.accessToken, name: 'power_bi_access_token' });
        return;
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          if (preferredFlow === 'popup') {
            try {
              const result = await $msal.acquireTokenPopup(mxbTokenRequest());
              TokenManager.setCachedToken({ token: result.accessToken, name: 'access_token' });

              const pbi = await $msal.acquireTokenSilent(powerBiTokenRequest());
              TokenManager.setCachedToken({
                token: pbi.accessToken,
                name: 'power_bi_access_token'
              });
              return;
            } catch (popupError) {
              captureErrorWithContext(popupError as Error, {
                operation: 'initial_signin_popup_interaction_required',
                flow: 'popup',
                errorType: popupError instanceof Error ? popupError.name : 'unknown',
                hasExistingAccounts: accounts.length > 0
              });
              throw popupError;
            }
          } else {
            // Use redirect flow for better reliability in non-embedded contexts
            await $msal.acquireTokenRedirect(mxbTokenRequest());
            // Note: execution will not continue here as the page will redirect
            return;
          }
        } else {
          // For other errors, continue to login flow
          Sentry.logger.warn('Silent token acquisition failed with non-interaction error', {
            log_source: 'msal',
            error: silentError
          });
          captureErrorWithContext(silentError as Error, {
            operation: 'initial_signin_silent_non_interaction',
            errorType: silentError instanceof Error ? silentError.name : 'unknown',
            hasExistingAccounts: accounts.length > 0
          });
        }
      }
    }

    // No existing account - initiate login
    if (preferredFlow === 'popup') {
      try {
        const result = await $msal.loginPopup(mxbTokenRequest());
        TokenManager.setCachedToken({ token: result.accessToken, name: 'access_token' });

        const pbi = await $msal.acquireTokenSilent(powerBiTokenRequest());
        TokenManager.setCachedToken({ token: pbi.accessToken, name: 'power_bi_access_token' });
      } catch (loginError) {
        captureErrorWithContext(loginError as Error, {
          operation: 'login_popup_new_account',
          flow: 'popup',
          errorType: loginError instanceof Error ? loginError.name : 'unknown'
        });
        throw loginError;
      }
    } else {
      await $msal.loginRedirect(mxbTokenRequest());
      // Note: execution will not continue here as the page will redirect
      return;
    }
  } catch (err) {
    Sentry.logger.error('Initial sign-in failed', { log_source: 'msal', error: err });
    captureErrorWithContext(err as Error, {
      operation: 'perform_initial_signin',
      flow: preferredFlow,
      errorType: err instanceof Error ? err.name : 'unknown'
    });
    throw err;
  }
}

/* ---------------------------------------------------------------------------
 * 7. MSAL events → track interaction status + token cache
 * ------------------------------------------------------------------------- */
export function setupMsalEventCallbacks(): void {
  $msal.addEventCallback(async ({ eventType }) => {
    switch (eventType) {
      case EventType.LOGIN_START:
      case EventType.LOGOUT_START:
      case EventType.ACQUIRE_TOKEN_START:
        isMsalInteractionInProgress = true;
        break;

      case EventType.LOGIN_SUCCESS:
      case EventType.LOGIN_FAILURE:
      case EventType.LOGOUT_SUCCESS:
      case EventType.LOGOUT_FAILURE:
      case EventType.ACQUIRE_TOKEN_SUCCESS:
      case EventType.ACQUIRE_TOKEN_FAILURE:
        isMsalInteractionInProgress = false;
        break;
    }

    if (eventType === EventType.LOGIN_SUCCESS || eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      setActiveAccount();
      if (isAuthenticated()) await refreshTokenIfNeeded('power_bi_access_token');
    }
  });
}

export function getIsMsalInteractiveInProgress(): boolean {
  return isMsalInteractionInProgress;
}

/* ---------------------------------------------------------------------------
 * 8. Power BI embed-token logic (***reportId now from router***)
 * ------------------------------------------------------------------------- */
export async function refreshPowerBiEmbedToken(): Promise<void> {
  if (!isAuthenticated()) {
    Sentry.logger.warn('Cannot refresh Power BI embed token: user not authenticated', {
      log_source: 'msal'
    });
    return;
  }

  const reportId = getCurrentReportId();
  if (!reportId) {
    Sentry.logger.warn('No reportId param on current route = skipping embed-token refresh', {
      log_source: 'msal'
    });
    return;
  }

  try {
    const { accessToken, expiresOn } = await $mxsFetch<{
      accessToken: string;
      expiresOn: Date;
    }>('/power-bi', { query: { reportId } });

    TokenManager.setCachedToken({ token: accessToken, name: 'power_bi_embed_access_token' });
    Sentry.logger.debug('Refreshed Power BI embed token', {
      log_source: 'msal',
      reportId,
      expiresOn
    });
  } catch (err) {
    Sentry.logger.error('Failed to refresh Power BI embed token', {
      log_source: 'msal',
      reportId,
      error: err
    });
    captureErrorWithContext(err as Error, {
      operation: 'refresh_powerbi_embed_token',
      reportId,
      isAuthenticated: isAuthenticated()
    });
  }
}

export function startPowerBiTokenRefreshInterval(): void {
  stopPowerBiTokenRefreshInterval();
  if (!isAuthenticated()) {
    Sentry.logger.warn('Cannot start embed-token timer: user not authenticated', {
      log_source: 'msal'
    });
    return;
  }

  powerBiTokenRefreshIntervalId = window.setInterval(refreshPowerBiEmbedToken, 900_000);
  void refreshPowerBiEmbedToken();
}

export function stopPowerBiTokenRefreshInterval(): void {
  if (powerBiTokenRefreshIntervalId !== null) {
    clearInterval(powerBiTokenRefreshIntervalId);
    powerBiTokenRefreshIntervalId = null;
  }
}

/* ---------------------------------------------------------------------------
 * 9. Cached data helpers (unchanged except minor tidy)
 * ------------------------------------------------------------------------- */
async function fetchMxBViews(): Promise<void> {
  try {
    const { views } = await $mxbFetchWithoutPreSignIn<ViewList>('/view/list');
    const dashboardViews = views.filter((v: View) => v.display_props.show_on_dashboard);
    localStorage.setItem('mxb_views', JSON.stringify(dashboardViews));
  } catch (err) {
    Sentry.logger.error('fetchMxBViews() failed', { log_source: 'msal', error: err });
    captureErrorWithContext(err as Error, {
      operation: 'fetch_mxb_views',
      isAuthenticated: isAuthenticated()
    });
    throw err; // Re-throw the error to be caught by the public functions
  }
}

async function fetchPowerBiReports(): Promise<void> {
  if (!isAuthenticated()) {
    Sentry.logger.warn('Cannot fetch Power BI reports: user not authenticated', {
      log_source: 'msal'
    });
    return;
  }

  const service = new PowerBiService(models.TokenType.Aad);
  const email = $msal.getActiveAccount()?.username;

  if (!email) {
    Sentry.logger.error('Active account email missing – aborting report fetch', {
      log_source: 'msal'
    });
    return;
  }

  try {
    const [workspaces, embedded] = await Promise.all([
      service.fetchWorkspaces(),
      $mxsFetch<PowerBiReport[]>(`/power-bi/${email}/get-embedded-reports`)
    ]);

    const workspaceReports: PowerBiReport[] = [];
    await Promise.all(
      workspaces.map(async ws => {
        workspaceReports.push(...(await service.fetchReports(ws)));
      })
    );

    const personal = await service.fetchPersonalReports();
    const unique = [...workspaceReports, ...embedded, ...personal].filter(
      (r, i, self) =>
        r.name && i === self.findIndex(x => x.id === r.id && x.tokenType === r.tokenType)
    );

    localStorage.setItem('power_bi_reports', JSON.stringify(unique));

    const hidden = await $mxbFetchWithoutPreSignIn<ApiResponse<string[], 'rows'>>('/view/hiddens');
    localStorage.setItem('hidden_reports', JSON.stringify(hidden.rows ?? []));
  } catch (err) {
    Sentry.logger.error('Error fetching Power BI reports', {
      log_source: 'msal',
      email,
      error: err
    });
    captureErrorWithContext(err as Error, {
      operation: 'fetch_powerbi_reports',
      email: $msal.getActiveAccount()?.username,
      isAuthenticated: isAuthenticated()
    });
    throw err; // Re-throw the error to be caught by the public functions
  }
}

async function fetchUserAccountAndPermissions(): Promise<string | void> {
  if (!isAuthenticated()) {
    Sentry.logger.warn('Cannot fetch user permissions: user not authenticated', {
      log_source: 'msal'
    });
    return;
  }

  const email = $msal.getActiveAccount()?.username;
  if (!email) {
    Sentry.logger.error('Active account email missing', { log_source: 'msal' });
    return;
  }

  let accountOrPermissionsChanged = false;

  try {
    const appName = getConfig('VITE_APP_NAME');
    const [user, mxbPerms, secPerms] = await Promise.all([
      $mxsFetch<SecurityAPIResponse<SecurityUser>>(`/users/by-email/${email}`),
      $mxsFetch<SecurityPermissionsAPIResponse>('/authorized-permissions', {
        method: 'POST',
        body: { email, appName }
      }),
      $mxsFetch<SecurityPermissionsAPIResponse>('/authorized-permissions', {
        method: 'POST',
        body: { email, appName: 'Matrix For Security' }
      })
    ]).then(response => {
      const savedUserDetails = localStorage.getItem('user');

      if (savedUserDetails !== JSON.stringify(response[0].data)) {
        accountOrPermissionsChanged = true;
      }
      return response;
    });

    localStorage.setItem('user', JSON.stringify(user.data));

    // Set Sentry user context after authentication
    setSentryUserContext(user.data);

    if (secPerms.data.data.length) localStorage.setItem('is_security_admin', 'true');

    const combined = [
      ...mxbPerms.data.data,
      ...secPerms.data.data.filter(
        p => !mxbPerms.data.data.some(e => e.permission === p.permission && e.page === p.page)
      )
    ];
    const savedPermissions = localStorage.getItem('user_permissions');

    if (savedPermissions !== JSON.stringify(combined)) {
      accountOrPermissionsChanged = true;
    }

    localStorage.setItem('user_permissions', JSON.stringify(combined));

    return accountOrPermissionsChanged ? 'CHANGED' : '';
  } catch (err) {
    Sentry.logger.error('fetchUserAccountAndPermissions() failed', {
      log_source: 'msal',
      email,
      error: err
    });
    captureErrorWithContext(err as Error, {
      operation: 'fetch_user_permissions',
      email,
      appName: getConfig('VITE_APP_NAME'),
      isAuthenticated: isAuthenticated()
    });
  }
}

/* ---------------------------------------------------------------------------
 * 10. Batched cached-data fetch
 * ------------------------------------------------------------------------- */

// **NEW EXPORTED FUNCTION** for the manual refresh button
export async function forceRefreshViewsAndReports(): Promise<void> {
  const isDevDomain = window.location.hostname.includes('dev.');
  naiveDiscrete.loadingBar.start();
  try {
    if (!isAuthenticated()) {
      Sentry.logger.warn('Skipping refresh: user not authenticated', { log_source: 'msal' });
      return;
    }

    // Explicitly fetch views and reports, handling errors locally to ensure concurrency
    await Promise.all([
      fetchMxBViews().catch(err =>
        Sentry.logger.error('Manual refresh of MxB Views failed', {
          log_source: 'msal',
          error: err
        })
      ),
      fetchPowerBiReports().catch(err =>
        Sentry.logger.error('Manual refresh of Power BI Reports failed', {
          log_source: 'msal',
          error: err
        })
      )
    ]);

    // Dispatch event to notify components listening for data updates
    window.dispatchEvent(new CustomEvent('update-data', { detail: { updateData: true } }));

    if (isDevDomain) {
      naiveDiscrete.notification.success({
        title: 'Data Refresh Complete',
        content: 'MxB Views and Power BI Reports have been updated.'
      });
    }
  } catch (err: any) {
    if (isDevDomain) {
      naiveDiscrete.notification.error({
        title: 'Manual Data Refresh Failed',
        content: err?.message || 'An error occurred during manual data refresh.'
      });
    }
  } finally {
    naiveDiscrete.loadingBar.finish();
    window.location.reload();
  }
}

// Existing function for system-triggered data fetch (e.g., on sign-in or permissions change)
export async function fetchCachedData(): Promise<void> {
  const isDevDomain = window.location.hostname.includes('dev.');

  naiveDiscrete.loadingBar.start();

  try {
    if (isAuthenticated()) {
      const result = await fetchUserAccountAndPermissions();
      if (result === 'CHANGED') {
        // Fetch views and reports concurrently on a permission change
        await Promise.all([
          fetchMxBViews().catch(err =>
            Sentry.logger.error('fetchMxBViews failed but the process continued', {
              log_source: 'msal',
              error: err
            })
          ),
          fetchPowerBiReports().catch(err =>
            Sentry.logger.error('fetchPowerBiReports failed but the process continued', {
              log_source: 'msal',
              error: err
            })
          )
        ]);

        if (isDevDomain) {
          naiveDiscrete.notification.success({
            title: 'Data Refreshed',
            content: 'User account and permissions have been updated.'
          });
        }
      }
    } else {
      Sentry.logger.warn('Skipping cached-data fetch: user not authenticated', {
        log_source: 'msal'
      });
      if (isDevDomain) {
        naiveDiscrete.notification.warning({
          title: 'Not Authenticated',
          content: 'Please sign in to access this feature.'
        });
      }
    }
  } catch (err: any) {
    if (isDevDomain) {
      naiveDiscrete.notification.error({
        title: 'Data Refresh Failed',
        content: err?.message || 'An error occurred while refreshing cached data.'
      });
    }
  } finally {
    naiveDiscrete.loadingBar.finish();
    window.dispatchEvent(new CustomEvent('update-data', { detail: { updateData: true } }));
  }
}

/* ---------------------------------------------------------------------------
 * 11. Public sign-in / out helpers
 * ------------------------------------------------------------------------- */
export async function handleTokenRefresh(): Promise<void> {
  if (!isAuthenticated()) return;

  try {
    await Promise.all([
      refreshTokenIfNeeded('access_token'),
      refreshTokenIfNeeded('power_bi_access_token')
    ]);
  } catch (refreshError) {
    captureErrorWithContext(refreshError as Error, {
      operation: 'handle_token_refresh_initial',
      errorType: refreshError instanceof Error ? refreshError.name : 'unknown'
    });
    try {
      const preferredFlow = getPreferredInteractionType();

      if (preferredFlow === 'popup') {
        const { accessToken } = await $msal.acquireTokenPopup(mxbTokenRequest());
        TokenManager.setCachedToken({ token: accessToken, name: 'access_token' });
      } else {
        await $msal.acquireTokenRedirect(mxbTokenRequest());
        // Execution stops here for redirect flow
      }
    } catch (err) {
      Sentry.logger.error('Token acquisition failed', { log_source: 'msal', error: err });
      captureErrorWithContext(err as Error, {
        operation: 'handle_token_refresh_fallback',
        flow: getPreferredInteractionType(),
        errorType: err instanceof Error ? err.name : 'unknown'
      });
      throw err;
    }
  }
}

export async function signIn(): Promise<void> {
  const isDevDomain = window.location.hostname.includes('dev.');
  naiveDiscrete.loadingBar.start();
  try {
    await performInitialSignIn();
    await refreshPowerBiEmbedToken();
    startPowerBiTokenRefreshInterval();
  } catch (err: any) {
    naiveDiscrete.loadingBar.error();
    captureErrorWithContext(err as Error, {
      operation: 'public_signin',
      errorType: err?.name || 'unknown',
      errorMessage: err?.message || 'Unknown error'
    });
    if (isDevDomain) {
      naiveDiscrete.notification.error({
        title: 'Sign In Failed',
        content: err?.message || 'An error occurred during sign in.'
      });
    }
    throw err;
  } finally {
    await fetchCachedData();
    naiveDiscrete.loadingBar.finish();
  }
}

export async function signOut(): Promise<void> {
  stopPowerBiTokenRefreshInterval();

  // Clear Sentry user context on logout
  setSentryUserContext(null);

  localStorage.clear();
  sessionStorage.clear();

  const isDevDomain = window.location.hostname.includes('dev.');

  naiveDiscrete.loadingBar.start();
  try {
    const preferredFlow = getPreferredInteractionType();

    if (preferredFlow === 'popup') {
      await $msal.logoutPopup({
        account: $msal.getActiveAccount(),
        postLogoutRedirectUri: `${window.location.origin}`
      });
    } else {
      await $msal.logoutRedirect({
        account: $msal.getActiveAccount(),
        postLogoutRedirectUri: `${window.location.origin}`
      });
    }
    if (isDevDomain) {
      naiveDiscrete.notification.success({
        title: 'Signed Out',
        content: 'You have been signed out.'
      });
    }
  } catch (err: any) {
    naiveDiscrete.loadingBar.error();
    captureErrorWithContext(err as Error, {
      operation: 'signout',
      flow: getPreferredInteractionType(),
      errorType: err?.name || 'unknown',
      errorMessage: err?.message || 'Unknown error'
    });
    if (isDevDomain) {
      naiveDiscrete.notification.error({
        title: 'Sign Out Failed',
        content: err?.message || 'An error occurred during sign out.'
      });
    }
    window.location.href = '/';
  } finally {
    naiveDiscrete.loadingBar.finish();
  }
}
